import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiBriefcase, FiCheckCircle, FiClock, FiEye, FiGlobe, FiKey, FiLoader, FiMail, FiPlus, FiSearch, FiTrash2, FiUser, FiUsers, FiX } from 'react-icons/fi';
import emailjs from '@emailjs/browser';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import useIsMobile from '../hooks/useIsMobile';
import { ROLES as ALL_ROLES } from '../utils/roleAccess';
import { onUsersChange, addUserToFirestore, deleteUserFromFirestore } from '../services/userService';

const EMAILJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE  || '';
const EMAILJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE || '';
const EMAILJS_KEY      = process.env.REACT_APP_EMAILJS_KEY      || '';

const load = (key, def) => {
  try { const s = localStorage.getItem(key); return s !== null ? JSON.parse(s) : def; }
  catch { return def; }
};

const generatePassword = () => {
  const u = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const l = 'abcdefghjkmnpqrstuvwxyz';
  const d = '23456789';
  const s = '@#$!%&';
  const all = u + l + d + s;
  const base =
    u[Math.floor(Math.random() * u.length)] +
    u[Math.floor(Math.random() * u.length)] +
    l[Math.floor(Math.random() * l.length)] +
    l[Math.floor(Math.random() * l.length)] +
    d[Math.floor(Math.random() * d.length)] +
    d[Math.floor(Math.random() * d.length)] +
    s[Math.floor(Math.random() * s.length)] +
    all[Math.floor(Math.random() * all.length)] +
    all[Math.floor(Math.random() * all.length)] +
    all[Math.floor(Math.random() * all.length)];
  return base.split('').sort(() => Math.random() - 0.5).join('');
};


const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

export default function AdminPanel() {
  const { theme } = useTheme();
  const { t } = useI18n();
  const isDark = theme === 'dark';
  const isMobile = useIsMobile();

  const [showForm, setShowForm] = useState(false);

  const [users, setUsers]   = useState([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [role,     setRole]     = useState('');
  const [customRole, setCustomRole] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [toast,    setToast]    = useState(null);
  const [modal,    setModal]    = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search,   setSearch]   = useState('');
  const [copied,   setCopied]   = useState(false);

  // Firestore real-time listener
  useEffect(() => {
    const unsub = onUsersChange((list, err) => {
      if (err) { showToast("Firestore xato: " + err.message, 'error'); }
      setUsers(list);
      setDbLoading(false);
    });
    return () => unsub();
  }, []); // eslint-disable-line

  const accentRaw   = load('smm_crm_accent', 'blue');
  const accentColor = { blue:'#3b82f6', purple:'#8b5cf6', green:'#22c55e', orange:'#f97316' }[accentRaw] || '#3b82f6';

  const bg   = isDark ? '#0f172a' : '#f1f5f9';
  const card = isDark ? '#1e293b' : '#ffffff';
  const bord = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const tp   = isDark ? '#f8fafc' : '#0f172a';
  const ts   = isDark ? '#94a3b8' : '#64748b';
  const ibg  = isDark ? '#0f172a' : '#f8fafc';
  const ibrd = isDark ? '#334155' : '#e2e8f0';

  const inp = {
    width: '100%', padding: '12px 14px', borderRadius: '12px',
    border: `1.5px solid ${ibrd}`, background: ibg, color: tp,
    fontSize: '15px', outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', transition: 'border-color 0.2s',
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const finalRole = role === 'Boshqa' ? customRole.trim() : role;
    if (!name.trim() || !email.trim() || !finalRole || !password.trim()) {
      showToast(t('admin.fillAll'), 'error'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast(t('admin.invalidEmail'), 'error'); return;
    }
    const existing = users.find(u => u.email.toLowerCase() === email.trim().toLowerCase());
    if (existing) { showToast(t('admin.emailExists'), 'error'); return; }

    const newUser = {
      id: Date.now() + '-' + Math.floor(Math.random() * 9999),
      name: name.trim(),
      email: email.trim().toLowerCase(),
      password,
      role: finalRole,
      createdAt: new Date().toISOString(),
    };

    try {
      await addUserToFirestore(newUser);
    } catch (err) {
      showToast("Firestore xato: " + err.message, 'error'); return;
    }

    setName(''); setEmail(''); setRole(''); setCustomRole(''); setPassword('');
    setShowForm(false);
    setModal({ user: newUser, emailStatus: 'sending' });
    showToast(t('admin.userAdded'));

    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        to_email: newUser.email, to_name: newUser.name,
        login_email: newUser.email, login_password: newUser.password,
        login_role: newUser.role, site_url: window.location.origin,
      }, { publicKey: EMAILJS_KEY });
      setModal(prev => prev ? { ...prev, emailStatus: 'sent' } : null);
      showToast(`${newUser.email} ga yuborildi!`);
    } catch (err) {
      const errMsg = err?.text || err?.message || JSON.stringify(err) || 'Noma\'lum xato';
      setModal(prev => prev ? { ...prev, emailStatus: 'error', errorMsg: errMsg } : null);
      showToast(`Email xato: ${errMsg}`, 'error');
    }
  };

  const handleSendEmail = async (user) => {
    setModal(prev => prev ? { ...prev, emailStatus: 'sending' } : null);
    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        to_email: user.email, to_name: user.name,
        login_email: user.email, login_password: user.password,
        login_role: user.role, site_url: window.location.origin,
      }, { publicKey: EMAILJS_KEY });
      setModal(prev => prev ? { ...prev, emailStatus: 'sent' } : null);
      showToast(`${user.email} ga yuborildi!`);
    } catch (err) {
      const errMsg = err?.text || err?.message || JSON.stringify(err) || 'Noma\'lum xato';
      setModal(prev => prev ? { ...prev, emailStatus: 'error', errorMsg: errMsg } : null);
      showToast(`Email xato: ${errMsg}`, 'error');
    }
  };

  const confirmDelete = (user) => setDeleteConfirm({ user });
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteUserFromFirestore(deleteConfirm.user.id);
    } catch (err) {
      showToast("O'chirishda xato: " + err.message, 'error'); return;
    }
    setDeleteConfirm(null);
    showToast(t('admin.userDeleted'));
  };

  const handleCopy = (user) => {
    const text = `Email: ${user.email}\nParol: ${user.password}\nMutaxasisligi: ${user.role}\nSayt: ${window.location.origin}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  const roleColor = (r) => ({
    'SMM Manager':'#3b82f6', 'Dizayner':'#8b5cf6', 'Video Montajchi':'#ef4444',
    'Kopirayter':'#22c55e', 'Analitik':'#f97316', 'Targeolog':'#06b6d4', 'Fotografchi':'#ec4899',
  }[r] || accentColor);

  /* ─── Render ─── */
  return (
    <div style={{ minHeight: '100vh', background: bg, padding: isMobile ? '16px 12px' : '24px', color: tp, fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' }}>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key="toast"
            initial={{ opacity:0, y:-20, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0, y:-20 }}
            style={{ position:'fixed', top:'16px', left:'50%', transform:'translateX(-50%)', zIndex:99999,
              padding:'12px 20px', borderRadius:'14px', maxWidth:'calc(100vw - 32px)',
              background: toast.type==='error' ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
              color:'#fff', fontWeight:700, fontSize:'13px', boxShadow:'0 16px 40px rgba(0,0,0,0.3)',
              display:'flex', alignItems:'center', gap:'8px', whiteSpace:'nowrap' }}>
            {toast.type==='error' ? <FiAlertCircle size={17} /> : <FiCheckCircle size={17} />} {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credentials modal */}
      <AnimatePresence>
        {modal && (
          <motion.div key="cred-modal" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center',
              background:'rgba(0,0,0,0.65)', backdropFilter:'blur(10px)', padding:'16px' }}>
            <motion.div initial={{ scale:0.88, y:24 }} animate={{ scale:1, y:0 }} exit={{ scale:0.88, y:24 }}
              style={{ width:'100%', maxWidth:'460px', borderRadius:'24px', padding: isMobile ? '24px 20px' : '32px',
                background:card, border:`1px solid ${bord}`, boxShadow:'0 30px 80px rgba(0,0,0,0.4)',
                color:tp, maxHeight:'90vh', overflowY:'auto' }}>

              <div style={{ width:'48px', height:'48px', borderRadius:'14px', background:'#22c55e20', color:'#22c55e', display:'grid', placeItems:'center', marginBottom:'14px' }}><FiCheckCircle size={22} /></div>
              <h3 style={{ margin:'0 0 8px', fontSize:'18px', fontWeight:700 }}>{t('admin.credentialsTitle')}</h3>

              {/* Email status */}
              <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 14px', borderRadius:'12px', marginBottom:'16px',
                background: modal.emailStatus==='sent' ? '#22c55e15' : modal.emailStatus==='error' ? '#ef444415' : '#3b82f615',
                border:`1px solid ${modal.emailStatus==='sent' ? '#22c55e30' : modal.emailStatus==='error' ? '#ef444430' : '#3b82f630'}` }}>
                <span style={{ display:'grid', placeItems:'center' }}>
                  {modal.emailStatus==='sent' ? <FiCheckCircle size={16} /> : modal.emailStatus==='error' ? <FiAlertCircle size={16} /> : <FiClock size={16} />}
                </span>
                <span style={{ fontSize:'13px', fontWeight:600,
                  color: modal.emailStatus==='sent' ? '#22c55e' : modal.emailStatus==='error' ? '#ef4444' : '#3b82f6' }}>
                  {modal.emailStatus==='sent' ? `${modal.user.email} ga email yuborildi!`
                    : modal.emailStatus==='error' ? `Xato: ${modal.errorMsg || 'Email yuborishda xato'}`
                    : 'Email yuborilmoqda...'}
                </span>
              </div>

              {/* Credentials */}
              <div style={{ borderRadius:'14px', background:ibg, border:`1.5px solid ${ibrd}`, marginBottom:'16px', overflow:'hidden' }}>
                {[
                  { icon:FiUser, label:t('admin.name'),     val:modal.user.name },
                  { icon:FiMail, label:t('admin.email'),    val:modal.user.email },
                  { icon:FiBriefcase, label:t('admin.role'), val:modal.user.role },
                  { icon:FiKey, label:t('admin.password'), val:modal.user.password },
                  { icon:FiGlobe, label:'Sayt',              val:window.location.origin },
                ].map((row, i, arr) => {
                  const RowIcon = row.icon;
                  return (
                    <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                      padding:'11px 14px', borderBottom: i < arr.length-1 ? `1px solid ${bord}` : 'none',
                      flexWrap: isMobile ? 'wrap' : 'nowrap', gap:'6px' }}>
                      <span style={{ color:ts, fontSize:'12px', fontWeight:600, display:'flex', alignItems:'center', gap:'5px' }}>
                        <RowIcon size={14} strokeWidth={1.8} aria-hidden="true" /> {row.label}
                      </span>
                      <span style={{ fontWeight:700, fontSize:'13px',
                        fontFamily: row.label===t('admin.password') ? 'monospace' : 'inherit',
                        color: row.label===t('admin.password') ? '#38bdf8' : tp,
                        wordBreak:'break-all', textAlign:'right' }}>{row.val}</span>
                    </div>
                  );
                })}
              </div>

              {/* Buttons */}
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                <div style={{ display:'flex', gap:'8px' }}>
                  <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }} type="button"
                    onClick={() => handleCopy(modal.user)}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'7px',
                      padding:'12px', borderRadius:'12px', border:`1.5px solid ${ibrd}`,
                      background:'transparent', color: copied ? '#22c55e' : tp,
                      fontWeight:600, fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                    <CopyIcon /> {copied ? 'Nusxalandi!' : t('admin.copy')}
                  </motion.button>
                  {modal.emailStatus === 'error' && (
                    <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }} type="button"
                      onClick={() => handleSendEmail(modal.user)}
                      style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'7px',
                        padding:'12px', borderRadius:'12px', border:'none',
                        background:'#ef4444', color:'#fff', fontWeight:600, fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                      <MailIcon /> Qayta yuborish
                    </motion.button>
                  )}
                </div>
                <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:0.98 }} type="button"
                  onClick={() => setModal(null)}
                  style={{ padding:'12px', borderRadius:'12px', border:`1.5px solid ${ibrd}`,
                    background:'transparent', color:ts, fontWeight:600, fontSize:'13px',
                    cursor:'pointer', fontFamily:'inherit' }}>
                  {t('admin.closeBtn')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirm modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div key="del-modal" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center',
              background:'rgba(0,0,0,0.6)', backdropFilter:'blur(8px)', padding:'16px' }}
            onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9 }}
              onClick={e => e.stopPropagation()}
              style={{ width:'100%', maxWidth:'360px', borderRadius:'20px', padding:'24px',
                background:card, border:`1px solid ${bord}`, boxShadow:'0 24px 60px rgba(0,0,0,0.35)', color:tp }}>
              <div style={{ width:'44px', height:'44px', borderRadius:'13px', background:'#ef444420', color:'#ef4444', display:'grid', placeItems:'center', marginBottom:'14px' }}><FiTrash2 size={19} /></div>
              <h3 style={{ margin:'0 0 8px', fontSize:'17px', fontWeight:700 }}>{t('admin.deleteTitle')}</h3>
              <p style={{ margin:'0 0 20px', color:ts, lineHeight:1.6, fontSize:'14px' }}>
                <strong>{deleteConfirm.user.name}</strong> ({deleteConfirm.user.email}) {t('admin.deleteBody')}
              </p>
              <div style={{ display:'flex', gap:'10px' }}>
                <button type="button" onClick={() => setDeleteConfirm(null)}
                  style={{ flex:1, padding:'12px', borderRadius:'12px', border:`1.5px solid ${ibrd}`,
                    background:'transparent', color:tp, fontWeight:600, cursor:'pointer', fontFamily:'inherit', fontSize:'14px' }}>
                  {t('admin.cancelBtn')}
                </button>
                <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }} type="button" onClick={handleDelete}
                  style={{ flex:1, padding:'12px', borderRadius:'12px', border:'none',
                    background:'#ef4444', color:'#fff', fontWeight:700, cursor:'pointer', fontFamily:'inherit', fontSize:'14px' }}>
                  {t('admin.deleteBtn')}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── PAGE ── */}
      <div style={{ maxWidth:'1200px', margin:'0 auto' }}>

        {/* Header */}
        <motion.div initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }}
          style={{ marginBottom:'20px', display:'flex', alignItems:'center',
            justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? '22px' : '26px', fontWeight:800,
              display:'flex', alignItems:'center', gap:'10px' }}>
              <FiUsers size={isMobile ? 22 : 25} aria-hidden="true" /> {t('admin.title')}
            </h1>
            <p style={{ margin:'4px 0 0', color:ts, fontSize:'13px' }}>{t('admin.subtitle')}</p>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            {/* Stats */}
            {[
              { label: t('admin.totalUsers'), val: users.length, color: accentColor },
              { label: t('admin.thisMonth'),
                val: users.filter(u => new Date(u.createdAt).getMonth() === new Date().getMonth()).length,
                color: '#22c55e' },
            ].map(s => (
              <div key={s.label} style={{ padding:'8px 14px', borderRadius:'12px',
                background: s.color+'15', border:`1px solid ${s.color}30`,
                display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'18px', fontWeight:800, color:s.color }}>{s.val}</span>
                <span style={{ fontSize:'11px', fontWeight:600, color:s.color }}>{s.label}</span>
              </div>
            ))}

            {/* Mobile: form toggle button */}
            {isMobile && (
              <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }} type="button"
                onClick={() => setShowForm(v => !v)}
                style={{ padding:'10px 18px', borderRadius:'12px', border:'none',
                  background: showForm ? '#64748b' : accentColor, color:'#fff',
                  fontWeight:700, fontSize:'13px', cursor:'pointer', fontFamily:'inherit',
                  display:'flex', alignItems:'center', gap:'6px' }}>
                {showForm ? <><FiX size={15} /> Yopish</> : <><FiPlus size={15} /> Qo'shish</>}
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Mobile: collapsible form */}
        <AnimatePresence>
          {isMobile && showForm && (
            <motion.div key="mobile-form"
              initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}
              style={{ overflow:'hidden', marginBottom:'16px' }}>
              <div style={{ borderRadius:'20px', background:card, border:`1px solid ${bord}`,
                padding:'20px', boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.25)' : '0 6px 24px rgba(0,0,0,0.06)' }}>
                <AddUserForm {...{ name, setName, email, setEmail, role, setRole, customRole, setCustomRole,
                  password, setPassword, showPwd, setShowPwd, handleAdd, inp, accentColor, ibrd, ibg, tp, ts, bord, t, ROLES: ALL_ROLES, isMobile }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: '1fr 360px', gap:'20px', alignItems:'flex-start' }}>

          {/* ── User list ── */}
          <motion.div initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.06 }}
            style={{ borderRadius:'20px', background:card, border:`1px solid ${bord}`,
              padding: isMobile ? '16px' : '24px',
              boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)',
              marginBottom: isMobile ? '16px' : 0 }}>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              marginBottom:'16px', gap:'10px', flexWrap:'wrap' }}>
              <h2 style={{ margin:0, fontSize:'15px', fontWeight:700 }}>
                {t('admin.userList')} <span style={{ color:ts, fontWeight:500 }}>({users.length})</span>
              </h2>
              <div style={{ position:'relative', width: isMobile ? '100%' : '190px' }}>
                <FiSearch size={15} style={{ position:'absolute', left:'11px', top:'50%', transform:'translateY(-50%)', color:ts }} aria-hidden="true" />
                <input type="text" placeholder="Qidirish..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ ...inp, width:'100%', borderRadius:'10px', padding:'9px 13px 9px 34px', fontSize:'13px' }}
                  onFocus={e => e.target.style.borderColor = accentColor}
                  onBlur={e => e.target.style.borderColor = ibrd} />
              </div>
            </div>

            {dbLoading ? (
              <div style={{ textAlign:'center', padding:'40px 16px', color:ts }}>
                <FiLoader size={25} style={{ marginBottom:'10px' }} />
                <p style={{ margin:0, fontWeight:600, fontSize:'14px' }}>Yuklanmoqda...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 16px', color:ts }}>
                <FiUsers size={34} style={{ marginBottom:'10px' }} />
                <p style={{ margin:0, fontWeight:600, fontSize:'14px' }}>
                  {users.length === 0 ? t('admin.noUsers') : t('admin.noResults')}
                </p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                <AnimatePresence>
                  {filtered.map((user, idx) => (
                    <motion.div key={user.id}
                      initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, x:-20 }}
                      transition={{ delay: idx * 0.03 }}
                      style={{ padding: isMobile ? '12px' : '14px 16px', borderRadius:'14px',
                        background:ibg, border:`1.5px solid ${bord}`, transition:'border-color 0.2s' }}
                      onMouseEnter={e => { if (!isMobile) e.currentTarget.style.borderColor = accentColor+'60'; }}
                      onMouseLeave={e => { if (!isMobile) e.currentTarget.style.borderColor = bord; }}>

                      <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                        {/* Avatar */}
                        <div style={{ width:'42px', height:'42px', borderRadius:'13px', flexShrink:0,
                          background: roleColor(user.role)+'20', border:`2px solid ${roleColor(user.role)}40`,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:'17px', fontWeight:800, color:roleColor(user.role) }}>
                          {user.name.charAt(0).toUpperCase()}
                        </div>

                        {/* Info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'7px', flexWrap:'wrap', marginBottom:'2px' }}>
                            <p style={{ margin:0, fontWeight:700, fontSize:'14px',
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                              maxWidth: isMobile ? '130px' : '200px' }}>{user.name}</p>
                            <span style={{ padding:'2px 8px', borderRadius:'999px',
                              background: roleColor(user.role)+'20', color:roleColor(user.role),
                              fontSize:'11px', fontWeight:700, flexShrink:0 }}>{user.role}</span>
                          </div>
                          <p style={{ margin:0, fontSize:'12px', color:ts,
                            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user.email}</p>
                          <p style={{ margin:'1px 0 0', fontSize:'11px', color:ts+'88' }}>
                            {t('admin.addedDate')}: {formatDate(user.createdAt)}
                          </p>
                        </div>

                        {/* Actions */}
                        <div style={{ display:'flex', gap: isMobile ? '5px' : '6px', flexShrink:0 }}>
                          <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} type="button"
                            onClick={() => setModal({ user, emailStatus: 'sent' })} title="Ko'rish"
                            style={{ width: isMobile ? '34px' : '32px', height: isMobile ? '34px' : '32px',
                              borderRadius:'10px', border:`1.5px solid ${ibrd}`,
                              background:'transparent', color:ts, cursor:'pointer',
                              display:'flex', alignItems:'center', justifyContent:'center', padding:0, fontSize:'14px' }}>
                              <FiEye size={15} />
                          </motion.button>
                          <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} type="button"
                            onClick={() => confirmDelete(user)} title={t('admin.deleteUser')}
                            style={{ width: isMobile ? '34px' : '32px', height: isMobile ? '34px' : '32px',
                              borderRadius:'10px', border:'1.5px solid rgba(239,68,68,0.3)',
                              background:'transparent', color:'#ef4444', cursor:'pointer',
                              display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                            <TrashIcon />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>

          {/* ── Desktop: Add user form ── */}
          {!isMobile && (
            <motion.div initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.1 }}
              style={{ borderRadius:'24px', background:card, border:`1px solid ${bord}`,
                padding:'24px', boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.3)' : '0 10px 40px rgba(0,0,0,0.06)',
                position:'sticky', top:'20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px' }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'13px', background:accentColor+'20',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>➕</div>
                <div>
                  <h2 style={{ margin:0, fontSize:'15px', fontWeight:700 }}>{t('admin.addUser')}</h2>
                  <p style={{ margin:'2px 0 0', fontSize:'12px', color:ts }}>{t('admin.addUserSub')}</p>
                </div>
              </div>
              <AddUserForm {...{ name, setName, email, setEmail, role, setRole, customRole, setCustomRole,
                password, setPassword, showPwd, setShowPwd, handleAdd, inp, accentColor, ibrd, ibg, tp, ts, bord, t, ROLES: ALL_ROLES, isMobile }} />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Extracted form component ── */
function AddUserForm({ name, setName, email, setEmail, role, setRole, customRole, setCustomRole,
  password, setPassword, showPwd, setShowPwd, handleAdd, inp, accentColor, ibrd, ibg, tp, ts, bord, t, ROLES, isMobile }) {

  const EyeIcon = ({ visible }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {visible
        ? (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>)
        : (<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>)}
    </svg>
  );

  return (
    <form onSubmit={handleAdd} style={{ display:'flex', flexDirection:'column', gap:'13px' }}>

      <div>
        <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:ts, marginBottom:'5px' }}>{t('admin.name')}</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="Shaxram Razzoqov" style={inp}
          onFocus={e => e.target.style.borderColor = accentColor}
          onBlur={e => e.target.style.borderColor = ibrd} />
      </div>

      <div>
        <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:ts, marginBottom:'5px' }}>{t('admin.email')} (Gmail)</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="user@gmail.com" style={inp}
          onFocus={e => e.target.style.borderColor = accentColor}
          onBlur={e => e.target.style.borderColor = ibrd} />
      </div>

      <div>
        <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:ts, marginBottom:'5px' }}>{t('admin.role')}</label>
        <select value={role} onChange={e => setRole(e.target.value)}
          style={{ ...inp, cursor:'pointer', appearance:'none',
            backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat:'no-repeat', backgroundPosition:'right 13px center' }}
          onFocus={e => e.target.style.borderColor = accentColor}
          onBlur={e => e.target.style.borderColor = ibrd}>
          <option value="">{t('admin.selectRole')}</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <AnimatePresence>
          {role === 'Boshqa' && (
            <motion.input initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
              type="text" value={customRole} onChange={e => setCustomRole(e.target.value)}
              placeholder={t('admin.customRole')} style={{ ...inp, marginTop:'8px' }}
              onFocus={e => e.target.style.borderColor = accentColor}
              onBlur={e => e.target.style.borderColor = ibrd} />
          )}
        </AnimatePresence>
      </div>

      <div>
        <label style={{ display:'block', fontSize:'12px', fontWeight:600, color:ts, marginBottom:'5px' }}>{t('admin.password')}</label>
        <div style={{ display:'flex', gap:'8px' }}>
          <div style={{ position:'relative', flex:1 }}>
            <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
              placeholder={t('admin.pwdPlaceholder')}
              style={{ ...inp, paddingRight:'40px', fontFamily: showPwd ? 'monospace' : 'inherit' }}
              onFocus={e => e.target.style.borderColor = accentColor}
              onBlur={e => e.target.style.borderColor = ibrd} />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)',
                background:'none', border:'none', color:ts, cursor:'pointer', display:'flex', alignItems:'center', padding:0 }}>
              <EyeIcon visible={showPwd} />
            </button>
          </div>
          <motion.button type="button" whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
            onClick={() => { setPassword(generatePassword()); setShowPwd(true); }}
            style={{ padding:'0 12px', borderRadius:'12px', border:`1.5px solid ${accentColor}`,
              background: accentColor+'15', color:accentColor, fontWeight:700, fontSize:'12px',
              cursor:'pointer', flexShrink:0, fontFamily:'inherit', whiteSpace:'nowrap' }}>
            🎲 {t('admin.randomBtn')}
          </motion.button>
        </div>
      </div>

      <motion.button type="submit" whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
        style={{ padding:'13px', borderRadius:'13px', border:'none',
          background: accentColor, color:'#fff', fontWeight:700, fontSize:'15px',
          cursor:'pointer', fontFamily:'inherit', marginTop:'2px',
          display:'flex', alignItems:'center', justifyContent:'center', gap:'8px',
          boxShadow:`0 6px 20px ${accentColor}44` }}>
        ➕ {t('admin.addBtn')}
      </motion.button>
    </form>
  );
}
